import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ScanEye, Loader2, CheckCircle2, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { api, errMsg } from '@/lib/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error('Reset link is missing a token');
  }, [token]);

  const strength = useMemo(() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 8) s += 1;
    if (/[A-Z]/.test(password)) s += 1;
    if (/[0-9]/.test(password)) s += 1;
    if (/[^A-Za-z0-9]/.test(password)) s += 1;
    return s; // 0..4
  }, [password]);
  const strengthLabel = ['Too short', 'Weak', 'Okay', 'Good', 'Strong'][strength];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Reset link is missing or invalid.');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      toast.success('Password reset. Please sign in with your new password.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      toast.error(errMsg(err, 'Reset failed. The link may be invalid or expired.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col hero-mist">
      <header className="mx-auto w-full max-w-[1240px] px-4 sm:px-6 lg:px-8 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"><ScanEye className="h-5 w-5" /></div>
          <div className="font-display text-base font-semibold">GEOrush</div>
        </Link>
      </header>
      <div className="flex-1 flex items-center justify-center px-4">
        <Card className="w-full max-w-md border card-elevated bg-card">
          <div className="p-6 sm:p-8">
            {done ? (
              <div className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <h1 className="mt-4 font-display text-2xl font-semibold tracking-tight">Password updated</h1>
                <p className="mt-2 text-sm text-muted-foreground">Redirecting to sign in…</p>
              </div>
            ) : !token ? (
              <div className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <h1 className="mt-4 font-display text-2xl font-semibold tracking-tight">Invalid reset link</h1>
                <p className="mt-2 text-sm text-muted-foreground">This link is missing a token. Please request a new one.</p>
                <Button className="mt-6" onClick={() => navigate('/forgot-password')}>Request new link</Button>
              </div>
            ) : (
              <>
                <h1 className="font-display text-2xl font-semibold tracking-tight">Choose a new password</h1>
                <p className="mt-1 text-sm text-muted-foreground">Pick something you’ll remember but that’s hard to guess.</p>
                <form onSubmit={onSubmit} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="pw">New password</Label>
                    <div className="relative">
                      <Input id="pw" type={show ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-reset-password-input" placeholder="At least 6 characters" />
                      <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle password visibility">
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div
                        className={`h-full transition-all ${strength <= 1 ? 'bg-rose-500' : strength === 2 ? 'bg-amber-500' : strength === 3 ? 'bg-emerald-500' : 'bg-emerald-600'}`}
                        style={{ width: `${(strength / 4) * 100}%` }}
                      />
                    </div>
                    <div className="text-[11px] text-muted-foreground">{password ? strengthLabel : 'Password strength'}</div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pw2">Confirm password</Label>
                    <Input id="pw2" type={show ? 'text' : 'password'} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="auth-reset-confirm-input" placeholder="Repeat new password" />
                    {confirm && confirm !== password && (
                      <div className="text-[11px] text-rose-600">Passwords do not match</div>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={loading || !password || password !== confirm} data-testid="auth-reset-submit-button">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
                  </Button>
                </form>
                <div className="mt-6 text-center text-sm text-muted-foreground">
                  Remembered it? <Link to="/login" className="text-[hsl(var(--accent))] font-medium hover:underline">Sign in</Link>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
